<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <PageHeadlineWithLink
      title="最近チェックしたギフト"
      link-title="削除する"
      text-align="left"
      @click-link="clickClearLink"
    />
    <div class="recent-view">
      <div v-if="isLoadingItems" class="loading-section">
        <Loading />
      </div>
      <div v-else class="recent-view__loaded">
        <div v-if="isNotEmptyResult">
          <div class="recent-view__items">
            <ItemBoxesConnected
              :items="items"
              @click-item-boxes-connected="onClickItemBox"
            />
          </div>
          <div class="recent-view__pagination">
            <Pagination
              class="pagination"
              :current-page="currentPage"
              :total-pages="totalPages"
              @clicked="onClickPageLink"
            />
          </div>
        </div>
        <div v-else-if="isLoadedItems" class="empty-result">
          <div class="empty-result__message">
            最近チェックしたギフトはありません
          </div>
        </div>
      </div>
      <div class="recent-view__to-giftee-top-area">
        <a href="/" class="recent-view__to-giftee-top-link text-link-to-top">
          gifteeトップへ戻る
        </a>
      </div>
      <div class="recent-view__to-page-top__area">
        <p class="recent-view__to-page-top__text subhead" @click="scrollToTop">
          ページトップへ ▲
        </p>
      </div>
    </div>
    <TextModal
      v-if="isClearModalActive"
      :title-text="'最近チェックしたギフトを削除'"
      :caption-text="'最近チェックしたギフトを\n削除してもよろしいですか？'"
      :primary-button-text="'削除する'"
      :cancel-button-text="'キャンセル'"
      @clicked-modal-cover="closeDeleteModal"
      @clicked-primary="clearRecentViewItems"
      @clicked-cancel="closeDeleteModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import url from 'url'
// NOTE: ネストしたクエリパラメータをパースしてくれる
// ref: https://github.com/ljharb/qs
// ref: https://tech-1natsu.hatenablog.com/entry/2018/07/04/175214
import Pagination from '../../organism/paginations/Pagination.vue'
import PageHeadlineWithLink from '../..//molecules/headlines/PageHeadlineWithLink.vue'
import ItemBoxesConnected from '../../molecules/item_boxes/ItemBoxesConnected.vue'
import Loading from '../../atoms/loadings/Loading.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import TextModal from '../../molecules/modals/text-modal.vue'
import * as Sentry from '@sentry/browser'

export default {
  components: {
    Pagination,
    PageHeadlineWithLink,
    ItemBoxesConnected,
    Loading,
    FeedbackError,
    TextModal
  },
  data() {
    return {
      items: [],
      isLoadingItems: false,
      isLoadedItems: false,
      currentPage: 1,
      totalPages: 1,
      showableErrorFeedback: false,
      errorMessage: '',
      isClearModalActive: false
    }
  },
  computed: {
    showEmptyResultContents() {
      return !this.isNotEmptyResult && this.isLoadedItems
    },
    isNotEmptyResult() {
      return this.items.length !== 0
    }
  },
  async mounted() {
    this.initializeParams()
    await this.getItems(this.currentPage, true)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function() {
      this.initializeParams()
      await this.getItems(this.currentPage, true)
    }.bind(this)
  },
  methods: {
    onClickItemBox(itemId) {
      dataLayer.push({
        event: 'click_view_history__item',
        via: 'view_history_list',
        item_id: itemId
      })
    },
    clickClearLink() {
      this.isClearModalActive = true
    },
    closeDeleteModal() {
      this.isClearModalActive = false
    },
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.currentPage = parsed.page_number ? Number(parsed.page_number) : 1
    },
    async onClickPageLink(page) {
      this.scrollToTop()
      await this.getItems(page)
      this.updateUrlFromParams()
    },
    createParamsObjectFromData(page) {
      const targetPageNumber = page ? page : this.currentPage
      const pageNumber = { page_number: targetPageNumber }
      return Object.assign({}, pageNumber)
    },
    updateUrlFromParams() {
      const params = this.createParamsObjectFromData()
      if (params.page_number === 1) {
        delete params.page_number
      }
      const paramsString = qs.stringify(params, { arrayFormat: 'brackets' })
      history.pushState(
        paramsString,
        '',
        !paramsString
          ? '/items/recent_view'
          : `/items/recent_view?${paramsString}`
      )
    },
    async getItems(pageNumber) {
      this.items = []
      this.isLoadingItems = true
      this.isLoadedItems = false
      try {
        const res = await axios.get(
          `/api/v1/pickup/items/recent_view?page=${pageNumber}`
        )
        const { current_page, total_pages } = res.data.meta
        this.currentPage = current_page
        this.totalPages = total_pages

        this.items = res.data.data
          .map(items => items.attributes)
          .map(item => ({
            id: item.id,
            linkUrl: `/items/${item.id}`,
            imageUrl: item.item_box_image_url,
            brandName: item.brand_name,
            itemName: item.name,
            itemPrice: item.min_price.toLocaleString(),
            isIncludingTax: item.is_including_tax,
            hasMultipleItemSkus: item.item_sku_count > 1,
            soldOut: item.sold_out
          }))
      } catch (error) {
        Sentry.captureException(error)
        this.showableErrorFeedback = true
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
      this.isLoadingItems = false
      this.isLoadedItems = true
    },
    async clearRecentViewItems() {
      this.isLoadingItems = true
      this.isLoadedItems = false
      try {
        await axios.delete(`/api/v1/pickup/items/clear_recent_view`)
        this.items = []
      } catch (error) {
        Sentry.captureException(error)
        this.showableErrorFeedback = true
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
      this.closeDeleteModal()
      this.isLoadingItems = false
      this.isLoadedItems = true
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    }
  }
}
</script>

<style lang="scss" scoped>
.recent-view {
  margin-top: 16px;

  &__items {
    background-color: $white;
  }

  &__pagination {
    margin-bottom: 8px;
    background-color: $white;

    .pagination {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &__to-giftee-top-area {
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 40px;
  }

  &__to-page-top {
    &__area {
      margin: auto;
      padding: 16px 0;
      background-color: $gray-2;
    }

    &__text {
      color: $link;
      display: inline;
    }
  }

  .loading-section {
    text-align: center;
    // height 29px で ローディングの下端の高さになる
    height: 29px;
    // padding-top 12px でローディングの上端の高さになるので、 12 + 24 = 52px でローディングの上端から40pxのpaddingになる。
    padding-top: 52px;
    padding-bottom: 40px;
  }

  .empty-result {
    height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    &__message {
      @extend %body;
      color: $gray-1;
    }
  }
}
</style>
